import React, { useEffect, useRef, useState } from "react";
import { SharedButton } from "../../../shared";
import ImageCropper from "../../FamilyConnect/MyProfile/ImageCropper";
import { uploadToCloudinary } from "../../../../lib/cloudinaryUpload";

// Converts a data URL (from the cropper) into a File for Cloudinary upload
const dataUrlToFile = (dataUrl, filename = "cropped.jpg") => {
  const [header, base64] = dataUrl.split(",");
  const mime = header.match(/:(.*?);/)[1];
  const bytes = atob(base64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new File([arr], filename, { type: mime });
};

function ImageUploader({ imageUrls, onImageUrlsChange, maxImages = 5, onUploadingChange }) {
  const [isUploading, setIsUploading] = useState(false);
  const [previews, setPreviews] = useState([]);
  const [cropUploading, setCropUploading] = useState(false);
  // A photo that is refused or fails used to vanish with no word why.
  const [uploadError, setUploadError] = useState(null);

  // Tells the form a photo is still on its way, so Post waits for it (the
  // same as the Bulletin and Scrapbook uploaders). Counted by the photos still
  // showing, so tapping × on one stuck on a lost connection frees Post again.
  const busy = previews.length > 0 || cropUploading;
  useEffect(() => {
    if (onUploadingChange) onUploadingChange(busy);
  }, [busy, onUploadingChange]);
  // Crop state — index of the image being cropped + its data URL
  const [cropIndex, setCropIndex] = useState(null);
  const [cropDataUrl, setCropDataUrl] = useState(null);
  const fileInputRef = useRef(null);

  // Upload files directly — no forced cropping
  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    const remainingSlots = maxImages - imageUrls.length - previews.length;
    const filesToUpload = files.slice(0, remainingSlots);
    if (filesToUpload.length === 0) return;

    // Instant local previews while uploading
    const newPreviews = filesToUpload.map((file) => ({
      localUrl: URL.createObjectURL(file),
      file,
      uploading: true,
    }));
    setPreviews((prev) => [...prev, ...newPreviews]);
    setIsUploading(true);
    setUploadError(null);

    for (const preview of newPreviews) {
      try {
        // Signed by our server; the old unsigned preset is no longer used.
        const data = await uploadToCloudinary(preview.file, { resourceType: "image" });

        setPreviews((prev) =>
          prev.filter((p) => p.localUrl !== preview.localUrl)
        );
        URL.revokeObjectURL(preview.localUrl);
        // A refused photo has no address. Adding it anyway put a blank image on
        // the listing.
        if (data.error || !data.secure_url) {
          console.error("Cloudinary refused the photo:", data.error?.message);
          setUploadError(`${preview.file.name} couldn't be added. Try a smaller JPG or PNG photo.`);
          continue;
        }
        onImageUrlsChange((current) => [...current, data.secure_url]);
      } catch (error) {
        console.error("Upload failed:", error);
        setUploadError(`${preview.file.name} didn't upload. Check your connection and try again.`);
        setPreviews((prev) =>
          prev.filter((p) => p.localUrl !== preview.localUrl)
        );
        URL.revokeObjectURL(preview.localUrl);
      }
    }

    setIsUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Fetch a remote image (Cloudinary URL) and convert to a data URL
  // so the cropper's Canvas export works without browser security blocks
  const openCropper = async (index) => {
    try {
      const response = await fetch(imageUrls[index]);
      const blob = await response.blob();
      const dataUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
      setCropIndex(index);
      setCropDataUrl(dataUrl);
    } catch (err) {
      console.error("Failed to load image for cropping:", err);
      setUploadError("That photo couldn't be opened for cropping. Please try again.");
    }
  };

  // After cropping, upload the cropped version and replace the original
  const handleCropComplete = async (croppedDataUrl) => {
    const index = cropIndex;
    setCropIndex(null);
    setCropDataUrl(null);

    // Upload the cropped version and replace the original URL
    setCropUploading(true);
    setUploadError(null);
    try {
      const data = await uploadToCloudinary(dataUrlToFile(croppedDataUrl, "cropped.jpg"), {
        resourceType: "image",
      });
      // A refused crop keeps the original photo rather than blanking it.
      if (data.error || !data.secure_url) {
        console.error("Cloudinary refused the cropped photo:", data.error?.message);
        setUploadError("The cropped photo couldn't be saved, so the original is kept.");
        return;
      }

      // Replace the old URL with the cropped version
      onImageUrlsChange((current) =>
        current.map((url, i) => (i === index ? data.secure_url : url))
      );
    } catch (error) {
      console.error("Crop upload failed:", error);
      setUploadError("The cropped photo didn't upload, so the original is kept.");
    } finally {
      setCropUploading(false);
    }
  };

  const handleRemoveImage = (index) => {
    onImageUrlsChange(imageUrls.filter((_, i) => i !== index));
  };

  const handleRemovePreview = (localUrl) => {
    setPreviews((prev) => prev.filter((p) => p.localUrl !== localUrl));
    URL.revokeObjectURL(localUrl);
  };

  const totalCount = imageUrls.length + previews.length;

  return (
    <div className="flex flex-col gap-y-[12px]">
      <div className="text-[14px] text-dupontGray">
        Photos ({totalCount}/{maxImages})
      </div>
      {uploadError && (
        <div role="alert" className="text-[14px] text-red-700 dark:text-red-400">
          {uploadError}
        </div>
      )}

      {/* Image previews — uploaded + uploading */}
      {(imageUrls.length > 0 || previews.length > 0) && (
        <div className="flex flex-row flex-wrap gap-[10px]">
          {/* Uploaded images — with remove and optional crop buttons */}
          {imageUrls.map((url, index) => (
            <div key={`uploaded-${index}`} className="relative group">
              <img
                src={url}
                alt={`Upload ${index + 1}`}
                className="w-[140px] h-[140px] object-cover rounded-[12px] border-2 border-dupontGold shadow-md"
              />
              {/* Remove button */}
              <button
                onClick={() => handleRemoveImage(index)}
                className="absolute -top-[8px] -right-[8px] bg-red-500 hover:bg-red-600 text-white rounded-full w-[24px] h-[24px] text-[13px] font-bold flex items-center justify-center cursor-pointer shadow-md transition-colors"
              >
                &times;
              </button>
              {/* Crop button — appears on hover */}
              <button
                onClick={() => openCropper(index)}
                className="absolute bottom-[6px] right-[6px] bg-black/60 hover:bg-black/80 text-white rounded-[6px] px-[8px] py-[4px] text-[12px] font-semibold cursor-pointer opacity-0 group-hover:opacity-100 transition-opacity"
              >
                Crop
              </button>
            </div>
          ))}
          {/* Uploading previews */}
          {previews.map((preview) => (
            <div key={preview.localUrl} className="relative">
              <img
                src={preview.localUrl}
                alt="Uploading..."
                className="w-[140px] h-[140px] object-cover rounded-[12px] border-2 border-dupontGold shadow-md opacity-70"
              />
              <div className="absolute inset-0 flex items-center justify-center rounded-[12px] bg-black/30">
                <div className="w-[28px] h-[28px] border-3 border-white border-t-transparent rounded-full animate-spin" />
              </div>
              <button
                onClick={() => handleRemovePreview(preview.localUrl)}
                className="absolute -top-[8px] -right-[8px] bg-red-500 hover:bg-red-600 text-white rounded-full w-[24px] h-[24px] text-[13px] font-bold flex items-center justify-center cursor-pointer shadow-md transition-colors"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload button */}
      {totalCount < maxImages && (
        <div>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            ref={fileInputRef}
            className="hidden"
          />
          <SharedButton
            disabled={isUploading}
            loadingText={isUploading ? "Uploading..." : undefined}
            color="secondary"
            onClick={() => fileInputRef.current?.click()}
          >
            Add Photos
          </SharedButton>
        </div>
      )}

      {/* Cropper modal — only appears when user clicks "Crop" on a thumbnail */}
      {cropIndex !== null && cropDataUrl && (
        <ImageCropper
          imageDataUrl={cropDataUrl}
          onCrop={handleCropComplete}
          shape="rectangle"
          onCancel={() => {
            setCropIndex(null);
            setCropDataUrl(null);
          }}
        />
      )}
    </div>
  );
}

export default ImageUploader;
